import { BBDTag, Horizontal, MaterialIcon, type MaterialSymbolIcon, Texto } from '@gravitate-js/excalibrr'
import type { ColDef, ColGroupDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community'
import dayjs from 'dayjs'
import React from 'react'
import { MultipleProductGroupCellRenderer } from '../../shared/LineItems/components'

export const getTransactionReportColumnDefs = (): (ColDef | ColGroupDef)[] => [
  // Invoice Group Level Columns
  {
    headerName: 'GROUP',
    groupId: 'group',
    marryChildren: true,
    openByDefault: true,
    children: [
      {
        headerName: 'Group Number',
        field: 'group_number',
        enableRowGroup: true,
        width: 140,
      },
      {
        headerName: 'Book Type',
        field: 'book_type',
        width: 120,
        cellStyle: { textTransform: 'uppercase' },
      },
      {
        headerName: 'Counterparty',
        field: 'counterparty_name',
        enableRowGroup: true,
        width: 180,
      },
      {
        headerName: 'Start Date',
        field: 'group_start_date',
        width: 120,
        valueFormatter: (params) => dayjs(params.value).format('MM/DD/YYYY'),
      },
      {
        headerName: 'End Date',
        field: 'group_end_date',
        width: 120,
        valueFormatter: (params) => dayjs(params.value).format('MM/DD/YYYY'),
      },
    ],
  },

  // Invoice Level Columns
  {
    headerName: 'INVOICE',
    groupId: 'invoice',
    marryChildren: true,
    openByDefault: true,
    children: [
      {
        headerName: 'Invoice Number',
        field: 'invoice_number',
        enableRowGroup: true,
        width: 140,
        cellStyle: { textTransform: 'uppercase', fontWeight: 'bold' },
      },
      {
        headerName: 'Type',
        field: 'invoice_type',
        width: 120,
        cellRenderer: (params: ICellRendererParams) => {
          const type = params.value || 'original'
          const iconDict = {
            original: 'document_scanner',
            rebill: 'currency_exchange',
            credit: 'credit_card',
          }

          let color = 'var(--theme-success)' // default color
          if (type === 'credit') {
            color = 'var(--theme-error)'
          } else if (type === 'rebill') {
            color = 'var(--theme-warning)'
          }

          return (
            <Horizontal style={{ overflow: 'visible', textTransform: 'capitalize' }} verticalCenter gap={4}>
              <MaterialIcon
                style={{ color }}
                size={14}
                icon={iconDict[type as keyof typeof iconDict] as MaterialSymbolIcon}
              />
              {type}
            </Horizontal>
          )
        },
      },
      {
        headerName: 'Invoice Date',
        field: 'invoice_date',
        width: 120,
        valueFormatter: (params) => dayjs(params.value).format('MM/DD/YYYY'),
      },
      {
        headerName: 'Sent Date',
        field: 'invoice_sent_date',
        width: 120,
        valueFormatter: (params) => (params.value ? dayjs(params.value).format('MM/DD/YYYY') : ''),
      },
    ],
  },

  // Transaction (Order) Level Columns
  {
    headerName: 'ORDER',
    groupId: 'order',
    marryChildren: true,
    openByDefault: true,
    children: [
      {
        headerName: 'Order Number',
        field: 'order_number',
        enableRowGroup: true,
        width: 140,
      },
      {
        headerName: 'Customer',
        field: 'customer_name',
        enableRowGroup: true,
        width: 180,
        cellRenderer: (params: ICellRendererParams) => (
          <Horizontal gap={8} verticalCenter>
            <MaterialIcon icon='store' style={{ color: 'var(--theme-color-2)' }} />
            <Texto>{params.value || 'N/A'}</Texto>
          </Horizontal>
        ),
      },
      {
        headerName: 'Carrier',
        field: 'carrier_name',
        enableRowGroup: true,
        width: 180,
        cellRenderer: (params: ICellRendererParams) => (
          <Horizontal gap={8} verticalCenter>
            <MaterialIcon icon='local_shipping' style={{ color: 'var(--theme-color-2)' }} />
            <Texto>{params.value}</Texto>
          </Horizontal>
        ),
      },
      {
        headerName: 'Destinations',
        field: 'destinations',
        width: 200,
        valueFormatter: (params: ValueFormatterParams) => params.value?.join(', ') || '',
      },
      {
        headerName: 'Product Groups',
        field: 'product_groups',
        minWidth: 140,
        cellRenderer: MultipleProductGroupCellRenderer,
      },
      {
        headerName: 'Distance',
        field: 'distance',
        width: 120,
        type: 'numericColumn',
        valueFormatter: (params) => `${params.value?.toFixed(2)} ${params.data.distance_uom}`,
      },
      {
        headerName: 'Volume',
        field: 'volume',
        width: 120,
        type: 'numericColumn',
        valueFormatter: (params) => `${params.value?.toLocaleString()} ${params.data.volume_uom}`,
      },
    ],
  },

  // Transaction Detail Level Columns
  {
    headerName: 'LINE ITEM',
    groupId: 'lineItem',
    marryChildren: true,
    openByDefault: true,
    children: [
      {
        headerName: 'PO',
        field: 'po',
        width: 120,
      },
      {
        headerName: 'Type',
        field: 'type',
        width: 140,
      },
      {
        headerName: 'Subtype',
        field: 'subtype',
        width: 140,
      },

      {
        headerName: 'Rate',
        field: 'rate',
        width: 120,
        type: 'numericColumn',
        valueFormatter: (params) =>
          params.value?.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
          }),
      },
      {
        headerName: 'Amount',
        field: 'amount',
        width: 120,
        type: 'numericColumn',
        valueFormatter: (params) =>
          params.value?.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
          }),
      },
      {
        headerName: 'Total',
        field: 'total',
        width: 120,
        type: 'numericColumn',
        valueFormatter: (params) =>
          params.value?.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
          }),
      },
      {
        headerName: 'UOM',
        field: 'uom',
        width: 100,
      },
      {
        headerName: 'Date',
        field: 'date',
        width: 120,
        valueFormatter: (params) => dayjs(params.value).format('MM/DD/YYYY'),
      },
    ],
  },
]
